import React, { useState, useEffect } from 'react';
import { getSections, createSection, updateSection, deleteSection, getCategories, createCategory, updateCategory, deleteCategory, getActiveSections } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { Plus, Pencil, Trash2, Layers, Tag } from 'lucide-react';
import { toast } from 'sonner';

export default function SectionsPage() {
  const [sections, setSections] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeSections, setActiveSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sectionDialogOpen, setSectionDialogOpen] = useState(false);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  
  const [sectionForm, setSectionForm] = useState({ name: '', code: '', order: 0 });
  const [categoryForm, setCategoryForm] = useState({ name: '', section_id: '' });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [sectionsRes, categoriesRes, activeSectionsRes] = await Promise.all([
        getSections({}),
        getCategories({}),
        getActiveSections()
      ]);
      setSections(sectionsRes.data);
      setCategories(categoriesRes.data);
      setActiveSections(activeSectionsRes.data);
    } catch (error) {
      toast.error('Error cargando datos');
    } finally {
      setLoading(false);
    }
  };

  // Sections handlers
  const handleSectionSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSection) {
        await updateSection(editingSection.id, sectionForm);
        toast.success('Sección actualizada');
      } else {
        await createSection(sectionForm);
        toast.success('Sección creada');
      }
      setSectionDialogOpen(false);
      resetSectionForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error guardando sección');
    }
  };

  const handleEditSection = (section) => {
    setEditingSection(section);
    setSectionForm({ name: section.name, code: section.code, order: section.order });
    setSectionDialogOpen(true);
  };

  const handleDeleteSection = async (id) => {
    if (!window.confirm('¿Desactivar esta sección?')) return;
    try {
      await deleteSection(id);
      toast.success('Sección desactivada');
      loadData();
    } catch (error) {
      toast.error('Error desactivando sección');
    }
  };

  const resetSectionForm = () => {
    setEditingSection(null);
    setSectionForm({ name: '', code: '', order: 0 });
  };

  // Categories handlers
  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { ...categoryForm };
      if (data.section_id === 'none') data.section_id = null;
      
      if (editingCategory) {
        await updateCategory(editingCategory.id, data);
        toast.success('Categoría actualizada');
      } else {
        await createCategory(data);
        toast.success('Categoría creada');
      }
      setCategoryDialogOpen(false);
      resetCategoryForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error guardando categoría');
    }
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryForm({ name: category.name, section_id: category.section_id || 'none' });
    setCategoryDialogOpen(true);
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm('¿Desactivar esta categoría?')) return;
    try {
      await deleteCategory(id);
      toast.success('Categoría desactivada');
      loadData();
    } catch (error) {
      toast.error('Error desactivando categoría');
    }
  };

  const resetCategoryForm = () => {
    setEditingCategory(null);
    setCategoryForm({ name: '', section_id: '' });
  };

  return (
    <div className="space-y-6" data-testid="sections-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 font-['Chivo']">Secciones y Categorías</h1>
        <p className="text-slate-500">Configura las secciones de tienda y categorías de productos</p>
      </div>

      <Tabs defaultValue="sections" className="space-y-4">
        <TabsList>
          <TabsTrigger value="sections">
            <Layers className="w-4 h-4 mr-2" /> Secciones
          </TabsTrigger>
          <TabsTrigger value="categories">
            <Tag className="w-4 h-4 mr-2" /> Categorías
          </TabsTrigger>
        </TabsList>

        {/* SECTIONS TAB */}
        <TabsContent value="sections">
          <div className="flex justify-end mb-4">
            <Dialog open={sectionDialogOpen} onOpenChange={(open) => { setSectionDialogOpen(open); if (!open) resetSectionForm(); }}>
              <DialogTrigger asChild>
                <Button className="bg-blue-600 hover:bg-blue-700" data-testid="add-section-btn">
                  <Plus className="w-4 h-4 mr-2" /> Nueva Sección
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{editingSection ? 'Editar Sección' : 'Nueva Sección'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSectionSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Nombre</Label>
                    <Input
                      value={sectionForm.name}
                      onChange={(e) => setSectionForm({...sectionForm, name: e.target.value})}
                      placeholder="Lácteos, Bebidas, Snacks..."
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Código</Label>
                    <Input
                      value={sectionForm.code}
                      onChange={(e) => setSectionForm({...sectionForm, code: e.target.value.toUpperCase()})}
                      placeholder="LAC, BEB, SNA..."
                      maxLength={5}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Orden</Label>
                    <Input
                      type="number"
                      value={sectionForm.order}
                      onChange={(e) => setSectionForm({...sectionForm, order: parseInt(e.target.value) || 0})}
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => setSectionDialogOpen(false)}>Cancelar</Button>
                    <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                      {editingSection ? 'Actualizar' : 'Crear'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <Card className="border-slate-200">
            <CardContent className="p-0">
              {loading ? (
                <div className="p-4 space-y-2">
                  {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12" />)}
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Orden</TableHead>
                      <TableHead>Nombre</TableHead>
                      <TableHead>Código</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead className="text-right">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sections.map((section) => (
                      <TableRow key={section.id}>
                        <TableCell className="font-mono">{section.order}</TableCell>
                        <TableCell className="font-medium">{section.name}</TableCell>
                        <TableCell><Badge variant="outline">{section.code}</Badge></TableCell>
                        <TableCell>
                          <Badge variant={section.active ? 'default' : 'secondary'}>
                            {section.active ? 'Activa' : 'Inactiva'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="icon" onClick={() => handleEditSection(section)}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDeleteSection(section.id)}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {sections.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                          No hay secciones. Crea la primera (ej: Lácteos, Bebidas).
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* CATEGORIES TAB */}
        <TabsContent value="categories">
          <div className="flex justify-end mb-4">
            <Dialog open={categoryDialogOpen} onOpenChange={(open) => { setCategoryDialogOpen(open); if (!open) resetCategoryForm(); }}>
              <DialogTrigger asChild>
                <Button className="bg-blue-600 hover:bg-blue-700" data-testid="add-category-btn">
                  <Plus className="w-4 h-4 mr-2" /> Nueva Categoría
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{editingCategory ? 'Editar Categoría' : 'Nueva Categoría'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCategorySubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Nombre</Label>
                    <Input
                      value={categoryForm.name}
                      onChange={(e) => setCategoryForm({...categoryForm, name: e.target.value})}
                      placeholder="Leche, Yogurt, Refrescos..."
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Sección (opcional)</Label>
                    <Select value={categoryForm.section_id} onValueChange={(v) => setCategoryForm({...categoryForm, section_id: v})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar sección" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Sin sección</SelectItem>
                        {activeSections.map(s => (
                          <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => setCategoryDialogOpen(false)}>Cancelar</Button>
                    <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                      {editingCategory ? 'Actualizar' : 'Crear'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <Card className="border-slate-200">
            <CardContent className="p-0">
              {loading ? (
                <div className="p-4 space-y-2">
                  {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12" />)}
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Nombre</TableHead>
                      <TableHead>Sección</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead className="text-right">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {categories.map((cat) => (
                      <TableRow key={cat.id}>
                        <TableCell className="font-medium">{cat.name}</TableCell>
                        <TableCell>
                          {cat.section_name ? (
                            <Badge variant="outline">{cat.section_name}</Badge>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant={cat.active ? 'default' : 'secondary'}>
                            {cat.active ? 'Activa' : 'Inactiva'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="icon" onClick={() => handleEditCategory(cat)}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDeleteCategory(cat.id)}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {categories.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center py-8 text-slate-500">
                          No hay categorías. Crea la primera (ej: Leche, Yogurt).
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
